import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCategory, subcatsByCategory } from '../../contracts/taxonomy';
import { categoryIcon, categoryClassLabel } from './categoryMeta';

// SCAN DETAIL — the detail panel for a selected category. Header (icon, name,
// class pill, guidance summary, score / 100) + a "what we measured" grid of
// subcategories; tapping a subcategory reveals its explanation note.
//
// Animations: the whole panel re-enters (fade/slide) when the category changes,
// and each subcategory note height-expands when tapped.

const SubBar = ({ score }) => (
  <div className="w-16 h-[3px] rounded-full bg-[rgba(255,255,255,0.1)] overflow-hidden shrink-0">
    <div
      className="h-full rounded-full"
      style={{
        width: score == null ? '0%' : `${Math.max(0, Math.min(100, score))}%`,
        background: 'linear-gradient(90deg, #9A7B40, #E6C083)',
      }}
    />
  </div>
);

const CategoryDetail = ({ categoryKey, score, summary, subScores = {}, subNotes = {}, className = '', flush = false }) => {
  const cat = getCategory(categoryKey);
  const subs = categoryKey ? subcatsByCategory(categoryKey) : [];
  const [openSub, setOpenSub] = useState(subs[0] ? subs[0].key : null);

  // Reset the open subcategory when the category changes.
  useEffect(() => {
    setOpenSub(subs[0] ? subs[0].key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  if (!cat) return null;
  const Icon = categoryIcon(categoryKey);

  return (
    <div
      className={[
        'p-5 lg:p-6',
        flush ? '' : 'bg-[rgba(32,32,31,0.55)] border border-hairline-soft rounded-card',
        className,
      ].filter(Boolean).join(' ')}
    >
      {/* Re-keying on categoryKey re-mounts → plays the enter transition. */}
      <motion.div
        key={categoryKey}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span
              className="w-12 h-12 rounded-[10px] border border-hairline-soft flex items-center justify-center shrink-0 text-aurum"
              style={{ background: 'linear-gradient(180deg, #3b3b39, #232322)' }}
              aria-hidden="true"
            >
              <Icon size={22} strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display text-bone" style={{ fontSize: 26, lineHeight: 1.1 }}>{cat.label}</span>
                <span
                  className="font-display uppercase tracking-[0.18em] text-aurum border border-[rgba(201,164,92,0.4)] rounded-full px-2.5 py-1"
                  style={{ fontSize: 10 }}
                >
                  {categoryClassLabel(categoryKey)}
                </span>
              </div>
              {summary && (
                <p className="font-display italic text-bone-dim m-0 mt-1.5" style={{ fontSize: 15, lineHeight: 1.4 }}>
                  {summary}
                </p>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display font-medium text-aurum tabular-nums" style={{ fontSize: 44, lineHeight: 1 }}>
              {score != null ? score : '—'}
            </div>
            <div className="font-display uppercase tracking-[0.18em] text-bone-dim" style={{ fontSize: 10 }}>/ 100</div>
          </div>
        </div>

        {/* subcategory grid */}
        <div className="font-display uppercase tracking-[0.2em] text-bone-dim mt-6 mb-1" style={{ fontSize: 11 }}>
          WHAT WE MEASURED · TAP FOR DETAIL
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          {subs.map((sub) => {
            const s = subScores[sub.key];
            const note = subNotes[sub.key];
            const open = openSub === sub.key;
            return (
              <div key={sub.key} className="border-b border-[rgba(255,255,255,0.06)]">
                <button
                  type="button"
                  onClick={() => setOpenSub(open ? null : sub.key)}
                  aria-expanded={open}
                  className={[
                    'w-full text-left flex items-center gap-4 py-3.5 cursor-pointer rounded-field px-2 -mx-2 transition-colors duration-200',
                    open ? 'bg-[rgba(255,255,255,0.03)]' : 'hover:bg-[rgba(255,255,255,0.02)]',
                  ].join(' ')}
                >
                  <span className="font-display font-medium text-aurum tabular-nums w-9 shrink-0" style={{ fontSize: 22 }}>
                    {s == null ? 'N/A' : s}
                  </span>
                  <span className="flex-1 min-w-0 font-display text-bone" style={{ fontSize: 16 }}>{sub.label}</span>
                  <SubBar score={s} />
                </button>
                <AnimatePresence initial={false}>
                  {open && note && (
                    <motion.div
                      key="note"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <p className="font-display text-[#B8B2A6] m-0 pb-3.5 px-2" style={{ fontSize: 14, lineHeight: 1.5 }}>
                        {note}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default CategoryDetail;
